import { Button, Input, Label } from "@/shared/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import { ROLE_LABELS, SHIFT_ROLES, type ShiftFilters, type ShiftRole } from "@/features/shifts/types";

const ALL_ROLES = "__all__";

interface Props {
  filters: ShiftFilters;
  onChange: (filters: ShiftFilters) => void;
}

export function ShiftFilterBar({ filters, onChange }: Props) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_0.9fr_1fr_auto]">
        <Field label="Role" htmlFor="filter-role">
          <Select
            value={filters.role ?? ALL_ROLES}
            onValueChange={(value) =>
              onChange({
                ...filters,
                role: value === ALL_ROLES ? undefined : (value as ShiftRole),
              })
            }
          >
            <SelectTrigger id="filter-role">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ROLES}>All roles</SelectItem>
              {SHIFT_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="From" htmlFor="filter-from">
          <Input
            id="filter-from"
            type="date"
            value={filters.date_from ?? ""}
            onChange={(e) => onChange({ ...filters, date_from: e.target.value || undefined })}
          />
        </Field>

        <Field label="To" htmlFor="filter-to">
          <Input
            id="filter-to"
            type="date"
            value={filters.date_to ?? ""}
            onChange={(e) => onChange({ ...filters, date_to: e.target.value || undefined })}
          />
        </Field>

        <Field label="Min pay" htmlFor="filter-min-pay">
          <Input
            id="filter-min-pay"
            type="number"
            min="0"
            step="0.50"
            placeholder="$0"
            value={filters.min_pay ?? ""}
            onChange={(e) => onChange({ ...filters, min_pay: e.target.value || undefined })}
          />
        </Field>

        <Field label="City" htmlFor="filter-city">
          <Input
            id="filter-city"
            type="text"
            placeholder="e.g. Ottawa"
            value={filters.city ?? ""}
            onChange={(e) => onChange({ ...filters, city: e.target.value || undefined })}
          />
        </Field>

        <div className="flex flex-col">
          {/* Spacer matches the label height so the button bottom-aligns
              with the input bottoms across the row. */}
          <span aria-hidden className="mb-1.5 select-none text-xs leading-none">
            &nbsp;
          </span>
          <Button variant="outline" size="md" onClick={() => onChange({})} className="w-full">
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-copy-muted">
        {label}
      </Label>
      {children}
    </div>
  );
}
